import { Chess } from "chess.js";

export class GameSession {
  #chess;

  constructor(pgn = "") {
    this.#chess = new Chess();
    if (pgn) {
      this.loadPgn(pgn);
    }
  }

  reset() {
    this.#chess.reset();
    return this.view();
  }

  loadFen(fen) {
    this.#chess.load(fen);
    return this.view();
  }

  loadPgn(pgn) {
    if (typeof pgn !== "string" || pgn.length > 100_000) {
      throw new Error("PGN must be text under 100 KB.");
    }
    const next = new Chess();
    next.loadPgn(pgn, { strict: false });
    this.#chess = next;
    return this.view();
  }

  legalMoves(square) {
    if (!/^[a-h][1-8]$/.test(square)) {
      return [];
    }
    return this.#chess
      .moves({ square, verbose: true })
      .map((move) => immutableMove(move));
  }

  applyMove(move) {
    const accepted = this.#chess.move(move);
    return { move: immutableMove(accepted), view: this.view() };
  }

  undo() {
    const move = this.#chess.undo();
    return move ? immutableMove(move) : null;
  }

  pgn() {
    return this.#chess.pgn({ maxWidth: 80, newline: "\n" });
  }
  fen() {
    return this.#chess.fen();
  }
  turn() {
    return this.#chess.turn();
  }
  isGameOver() {
    return this.#chess.isGameOver();
  }

  view() {
    const board = this.#chess.board().flatMap((rank) =>
      rank.map((piece) =>
        piece
          ? Object.freeze({
              square: piece.square,
              type: piece.type,
              color: piece.color,
            })
          : null,
      ),
    );
    const history = this.#chess.history({ verbose: true }).map(immutableMove);
    const status = gameStatus(this.#chess);
    return Object.freeze({
      fen: this.#chess.fen(),
      pgn: this.pgn(),
      turn: this.#chess.turn(),
      board: Object.freeze(board),
      history: Object.freeze(history),
      status,
    });
  }
}

function immutableMove(move) {
  return Object.freeze({
    color: move.color,
    from: move.from,
    to: move.to,
    piece: move.piece,
    captured: move.captured,
    promotion: move.promotion,
    flags: move.flags,
    san: move.san,
    lan: move.lan,
  });
}

function gameStatus(chess) {
  let reason = null;
  let result = null;
  if (chess.isCheckmate()) {
    reason = "checkmate";
    result = chess.turn() === "w" ? "0-1" : "1-0";
  } else if (chess.isStalemate()) {
    reason = "stalemate";
    result = "1/2-1/2";
  } else if (chess.isThreefoldRepetition()) {
    reason = "threefold repetition";
    result = "1/2-1/2";
  } else if (chess.isDrawByFiftyMoves()) {
    reason = "fifty-move rule";
    result = "1/2-1/2";
  } else if (chess.isInsufficientMaterial()) {
    reason = "insufficient material";
    result = "1/2-1/2";
  } else if (chess.isDraw()) {
    reason = "draw";
    result = "1/2-1/2";
  }
  return Object.freeze({
    gameOver: chess.isGameOver(),
    check: chess.isCheck(),
    checkmate: chess.isCheckmate(),
    reason,
    result,
  });
}
